import React, { useState } from "react";
import {
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  Gift as GiftIcon,
  Sparkles,
  Moon,
  Heart,
  FileText,
  X,
} from "lucide-react";
import { firebaseStorage } from "../utils/firebase";
import PaymentModal from "./PaymentModal";
import ImageUploadField from "./ImageUploadField";
import {
  getTemplatesByRelationship,
  applyTemplate,
} from "../utils/messageTemplates";

const GiftCreation = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [showPayment, setShowPayment] = useState(false);
  const [gift, setGift] = useState({
    recipientName: "",
    senderName: "",
    relationship: "",
    openingMessage: "",
    lanterns: Array.from({ length: 30 }, (_, i) => ({
      day: i + 1,
      message: "",
      imageURL: "",
    })),
    planType: "free",
    paid: false,
    password: "", // كلمة السر الاختيارية
    hasPassword: false, // هل الهدية محمية بكلمة سر؟
    accessToken: "", // Magic Link token
  });
  const [currentLantern, setCurrentLantern] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showTikTokFollow, setShowTikTokFollow] = useState(false);
  const [hasClickedTikTok, setHasClickedTikTok] = useState(false);

  const relationships = [
    { value: "mother", label: "أمي / Mother" },
    { value: "father", label: "أبي / Father" },
    { value: "spouse", label: "زوجي/زوجتي / Spouse" },
    { value: "friend", label: "صديقي / Friend" },
    { value: "sibling", label: "أخي/أختي / Sibling" },
    { value: "custom", label: "مخصص / Custom" },
  ];

  const handlePlanSelect = (planType) => {
    if (planType === "free") {
      setGift({ ...gift, planType, paid: true });
    } else {
      setGift({ ...gift, planType, paid: false });
      setShowPayment(true);
    }
  };

  const handlePaymentConfirm = (paymentProof) => {
    setGift({ ...gift, paid: true, paymentProof });
    setShowPayment(false);
  };

  const handleApplyTemplate = () => {
    if (!gift.relationship) {
      alert("اختر العلاقة أولاً!");
      return;
    }
    const template = getTemplatesByRelationship(gift.relationship);
    if (template) {
      const updatedGift = applyTemplate(gift.relationship, gift);
      setGift(updatedGift);
      setShowTemplates(false);
      alert("تم تطبيق القالب بنجاح! ✅");
    }
  };

  const generateMagicToken = () => {
    return `ML-${Math.random().toString(36).substring(2, 15)}-${Date.now().toString(36)}`;
  };

  const saveGift = async () => {
    // إذا كانت هدية مجانية، اعرض modal المتابعة على TikTok أولاً
    if (gift.planType === "free") {
      setShowTikTokFollow(true);
      return;
    }

    // استمر مع الحفظ للخطط المدفوعة
    await proceedWithSave();
  };

  const proceedWithSave = async () => {
    const maxLanterns =
      gift.planType === "free" ? 5 : gift.planType === "basic" ? 15 : 30;

    const magicToken = gift.hasPassword ? generateMagicToken() : null;

    const newGift = {
      recipientName: gift.recipientName || "",
      senderName: gift.senderName || "",
      relationship: gift.relationship || "",
      openingMessage: gift.openingMessage || "",
      lanterns: (gift.lanterns || []).slice(0, maxLanterns),
      planType: gift.planType || "free",
      paid: gift.paid || false,
      paymentProof: gift.paymentProof,
      paymentStatus: gift.planType === "free" ? "approved" : "pending",
      hasPassword: gift.planType !== "free" && gift.hasPassword,
      password:
        gift.planType !== "free" && gift.hasPassword ? gift.password : null,
      accessToken: magicToken,
      createdAt: Date.now(),
    };

    try {
      setIsUploading(true);

      const result = await firebaseStorage.saveGift(newGift);

      if (result.isFree) {
        onComplete({
          id: result.id,
          trackingCode: null,
          planType: gift.planType,
          magicLink: magicToken
            ? `${window.location.origin}?gift=${result.id}&token=${magicToken}`
            : null,
          isFree: true,
        });
      } else {
        onComplete({
          id: result.id,
          trackingCode: result.trackingCode,
          planType: gift.planType,
          magicLink: magicToken
            ? `${window.location.origin}?gift=${result.id}&token=${magicToken}`
            : null,
          isFree: false,
        });
      }
    } catch (error) {
      console.error("Error saving gift:", error);
      alert("حدث خطأ أثناء حفظ الهدية. حاول مرة أخرى.");
    } finally {
      setIsUploading(false);
    }
  };

  const maxLanterns =
    gift.planType === "free" ? 5 : gift.planType === "basic" ? 15 : 30;
  const canProceed = gift.planType === "free" || gift.paid;

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-indigo-950 via-purple-950 to-indigo-900 text-white py-8 px-4"
      dir="rtl"
    >
      {showPayment && gift.planType !== "free" && (
        <PaymentModal
          planType={gift.planType}
          onConfirm={handlePaymentConfirm}
          onCancel={() => setShowPayment(false)}
        />
      )}

      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <div className="flex justify-between mb-2 text-sm text-right">
            <span>الخطوة {step} من 5</span>
            <span>Step {step} of 5</span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500 transition-all duration-500"
              style={{ width: `${(step / 5) * 100}%` }}
            />
          </div>
        </div>

        {/* Step 1: Plan Selection */}
        {step === 1 && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 space-y-6 text-right">
            <h2 className="text-3xl font-bold text-center mb-6">اختر خطتك</h2>
            <h3 className="text-xl text-center text-purple-200 mb-8">
              Choose Your Plan
            </h3>

            <div className="grid md:grid-cols-3 gap-4">
              <div
                onClick={() => handlePlanSelect("free")}
                className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${gift.planType === "free" ? "border-yellow-400 bg-yellow-400/20" : "border-white/30 bg-white/5 hover:border-white/50"}`}
              >
                <h4 className="text-xl font-bold mb-2">مجاني</h4>
                <p className="text-lg mb-1">Free</p>
                <p className="text-3xl font-bold mb-4">0 جنيه</p>
                <ul className="space-y-2 text-sm">
                  <li>✓ 5 هدايا</li>
                  <li>✓ رسائل فقط</li>
                </ul>
              </div>

              <div
                onClick={() => handlePlanSelect("basic")}
                className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${gift.planType === "basic" ? "border-yellow-400 bg-yellow-400/20" : "border-white/30 bg-white/5 hover:border-white/50"}`}
              >
                <h4 className="text-xl font-bold mb-2">أساسي</h4>
                <p className="text-lg mb-1">Basic</p>
                <p className="text-3xl font-bold mb-4">50 جنيه</p>
                <ul className="space-y-2 text-sm">
                  <li>✓ 15 هدية</li>
                  <li>✓ رسائل + صور</li>
                </ul>
                {gift.planType === "basic" && gift.paid && (
                  <div className="mt-3 flex items-center gap-2 text-green-400">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-xs">تم الدفع</span>
                  </div>
                )}
              </div>

              <div
                onClick={() => handlePlanSelect("premium")}
                className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${gift.planType === "premium" ? "border-yellow-400 bg-yellow-400/20" : "border-white/30 bg-white/5 hover:border-white/50"}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xl font-bold">مميز</h4>
                  <Sparkles className="w-5 h-5 text-yellow-400" />
                </div>
                <p className="text-lg mb-1">Premium</p>
                <p className="text-3xl font-bold mb-4">100 جنيه</p>
                <ul className="space-y-2 text-sm">
                  <li>✓ 30 هدية</li>
                  <li>✓ رسائل + صور + أدعية</li>
                </ul>
                {gift.planType === "premium" && gift.paid && (
                  <div className="mt-3 flex items-center gap-2 text-green-400">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-xs">تم الدفع</span>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => setStep(2)}
              disabled={!canProceed}
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-indigo-950 font-bold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {!canProceed ? "ادفع الأول عشان تكمل" : "التالي / Next"}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Step 2: Basic Info */}
        {step === 2 && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 space-y-6 text-right">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold">بيانات الهدية</h2>
              <button
                onClick={() => setShowPreview(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-bold transition-all"
              >
                👁️ معاينة
              </button>
            </div>
            <h3 className="text-xl text-center text-purple-200 mb-8">
              Gift Information
            </h3>

            <div>
              <label className="block text-lg mb-2 text-yellow-300">
                من (اسمك)
              </label>
              <input
                type="text"
                value={gift.senderName}
                onChange={(e) =>
                  setGift({ ...gift, senderName: e.target.value })
                }
                className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400 text-right"
                placeholder="اكتب اسمك هنا"
              />
            </div>

            <div>
              <label className="block text-lg mb-2 text-yellow-300">
                إلى (المستلم)
              </label>
              <input
                type="text"
                value={gift.recipientName}
                onChange={(e) =>
                  setGift({ ...gift, recipientName: e.target.value })
                }
                className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400 text-right"
                placeholder="مثال: أمي الحبيبة"
              />
            </div>

            <div>
              <label className="block text-lg mb-2 text-right">العلاقة</label>
              <select
                value={gift.relationship}
                onChange={(e) =>
                  setGift({ ...gift, relationship: e.target.value })
                }
                className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white focus:outline-none focus:border-yellow-400 text-right"
              >
                <option value="">اختر...</option>
                {relationships.map((r) => (
                  <option
                    key={r.value}
                    value={r.value}
                    className="bg-indigo-900"
                  >
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            {/* كلمة السر - للخطط المدفوعة فقط */}
            {gift.planType !== "free" && (
              <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="hasPassword"
                    checked={gift.hasPassword}
                    onChange={(e) =>
                      setGift({
                        ...gift,
                        hasPassword: e.target.checked,
                        password: e.target.checked ? gift.password : "",
                      })
                    }
                    className="w-5 h-5 rounded"
                  />
                  <label
                    htmlFor="hasPassword"
                    className="text-yellow-300 font-bold cursor-pointer"
                  >
                    🔒 حماية الهدية بكلمة سر (اختياري)
                  </label>
                </div>

                {gift.hasPassword && (
                  <div>
                    <label className="block text-sm mb-2 text-yellow-200">
                      كلمة السر
                    </label>
                    <input
                      type="text"
                      value={gift.password}
                      onChange={(e) =>
                        setGift({ ...gift, password: e.target.value })
                      }
                      className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400 text-center"
                      placeholder="مثال: ramadan2026"
                      maxLength={20}
                    />
                    <p className="text-xs text-yellow-200 mt-2">
                      💡 المستلم سيحتاج إدخال هذه الكلمة لفتح الهدية
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={() => setStep(1)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5 inline" /> السابق
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={
                  !gift.recipientName ||
                  !gift.senderName ||
                  !gift.relationship ||
                  (gift.hasPassword && !gift.password)
                }
                className="flex-1 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-indigo-950 font-bold py-3 rounded-lg disabled:opacity-50"
              >
                التالي <ChevronRight className="w-5 h-5 inline" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Opening Message & Templates */}
        {step === 3 && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 space-y-6 text-right">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold">رسالة الافتتاح</h2>
              <button
                onClick={() => setShowPreview(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-bold transition-all"
              >
                👁️ معاينة
              </button>
            </div>
            <h3 className="text-xl text-center text-purple-200 mb-8">
              Opening Message
            </h3>

            <div>
              <label className="block text-lg mb-2">رسالة الافتتاح</label>
              <textarea
                value={gift.openingMessage}
                onChange={(e) =>
                  setGift({ ...gift, openingMessage: e.target.value })
                }
                rows={4}
                className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400 text-right"
                placeholder="رسالة جميلة لبداية رمضان..."
              />
            </div>

            {/* اقتراح استخدام القوالب */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6">
              <div className="flex items-start gap-3 mb-4">
                <FileText className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-lg font-bold text-green-300 mb-2">
                    هل تريد توفير الوقت؟ 🚀
                  </h4>
                  <p className="text-green-200 text-sm mb-3">
                    استخدم القوالب الجاهزة! لدينا {maxLanterns} رسالة مكتوبة
                    خصيصاً لـ{" "}
                    {
                      relationships.find((r) => r.value === gift.relationship)
                        ?.label
                    }
                  </p>
                  <p className="text-xs text-green-300 mb-4">
                    💡 ستملأ رسالة الافتتاح وجميع رسائل الأيام تلقائياً، ويمكنك
                    التعديل عليها لاحقاً
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowTemplates(true)}
                className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-bold transition-all flex items-center justify-center gap-2"
              >
                <FileText className="w-5 h-5" />
                استخدم القوالب الجاهزة ({maxLanterns} رسالة)
              </button>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setStep(2)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5 inline" /> السابق
              </button>
              <button
                onClick={() => setStep(4)}
                className="flex-1 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-indigo-950 font-bold py-3 rounded-lg"
              >
                التالي <ChevronRight className="w-5 h-5 inline" />
              </button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6 text-right">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
              <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
                <h2 className="text-2xl font-bold">املأ الهدايا</h2>
                <button
                  onClick={() => setShowPreview(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-bold transition-all"
                >
                  👁️ معاينة
                </button>
              </div>
              <p className="text-center text-purple-200 mb-6">
                ({currentLantern + 1}/{maxLanterns})
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-lg mb-2">
                    يوم {currentLantern + 1} - الرسالة
                  </label>
                  <textarea
                    value={gift.lanterns?.[currentLantern]?.message || ""}
                    onChange={(e) => {
                      const lanterns = [...(gift.lanterns || [])];
                      lanterns[currentLantern] = {
                        ...lanterns[currentLantern],
                        message: e.target.value,
                      };
                      setGift({ ...gift, lanterns });
                    }}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400 text-right"
                    placeholder="اكتب رسالتك الجميلة هنا..."
                  />
                </div>

                {gift.planType !== "free" && (
                  <div>
                    <label className="block text-lg mb-2">
                      صورة اليوم (اختياري)
                    </label>
                    <ImageUploadField
                      value={gift.lanterns?.[currentLantern]?.imageURL || ""}
                      onChange={(url) => {
                        const lanterns = [...(gift.lanterns || [])];
                        lanterns[currentLantern] = {
                          ...lanterns[currentLantern],
                          imageURL: url,
                        };
                        setGift({ ...gift, lanterns });
                      }}
                      dayNumber={currentLantern + 1}
                    />
                  </div>
                )}

                {gift.planType === "premium" && (
                  <div>
                    <label className="block text-lg mb-2">دعاء (اختياري)</label>
                    <textarea
                      value={gift.lanterns?.[currentLantern]?.duaa || ""}
                      onChange={(e) => {
                        const lanterns = [...(gift.lanterns || [])];
                        lanterns[currentLantern] = {
                          ...lanterns[currentLantern],
                          duaa: e.target.value,
                        };
                        setGift({ ...gift, lanterns });
                      }}
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400 text-right"
                      placeholder="دعاء جميل لهذا اليوم..."
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-4 mt-6">
                <button
                  onClick={() =>
                    setCurrentLantern(Math.max(0, currentLantern - 1))
                  }
                  disabled={currentLantern === 0}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-lg disabled:opacity-50"
                >
                  <ChevronLeft className="w-5 h-5 inline" /> السابق
                </button>
                <button
                  onClick={() =>
                    currentLantern < maxLanterns - 1
                      ? setCurrentLantern(currentLantern + 1)
                      : setStep(5)
                  }
                  className="flex-1 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-indigo-950 font-bold py-3 rounded-lg"
                >
                  {currentLantern === maxLanterns - 1 ? "المعاينة" : "التالي"}{" "}
                  <ChevronRight className="w-5 h-5 inline" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Preview */}
        {step === 5 && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 space-y-6 text-right">
            <h2 className="text-3xl font-bold text-center mb-6">
              المعاينة النهائية
            </h2>
            <div className="bg-white/5 rounded-lg p-6 space-y-4">
              <p className="text-yellow-400">
                <strong>من:</strong> {gift.senderName}
              </p>
              <p>
                <strong>إلى:</strong> {gift.recipientName}
              </p>
              <p>
                <strong>العلاقة:</strong>{" "}
                {
                  relationships.find((r) => r.value === gift.relationship)
                    ?.label
                }
              </p>
              <p>
                <strong>الخطة:</strong> {gift.planType}
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setStep(3)}
                className="flex-1 bg-white/10 py-3 rounded-lg"
              >
                تعديل
              </button>
              <button
                onClick={saveGift}
                disabled={isUploading}
                className="flex-1 bg-yellow-400 text-indigo-950 font-bold py-3 rounded-lg transition-transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isUploading ? (
                  <>
                    <div className="w-5 h-5 border-3 border-indigo-950 border-t-transparent rounded-full animate-spin"></div>
                    <span>جاري تجهيز الهدية...</span>
                  </>
                ) : (
                  <>
                    <GiftIcon className="w-5 h-5" />
                    <span>إنشاء الهدية</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Templates Modal */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 overflow-y-auto p-4">
          <div className="max-w-2xl mx-auto">
            <div
              className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-xl p-8 text-right"
              dir="rtl"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <FileText className="w-6 h-6" />
                  قوالب الرسائل الجاهزة
                </h2>
                <button
                  onClick={() => setShowTemplates(false)}
                  className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {!gift.relationship ? (
                <div className="text-center py-8">
                  <p className="text-purple-200 mb-4">
                    اختر العلاقة في الخطوة 2 أولاً!
                  </p>
                  <button
                    onClick={() => {
                      setShowTemplates(false);
                      setStep(2);
                    }}
                    className="bg-yellow-400 hover:bg-yellow-500 text-indigo-950 font-bold px-6 py-3 rounded-lg"
                  >
                    العودة للخطوة 2
                  </button>
                </div>
              ) : (
                <div>
                  <div className="bg-white/10 rounded-lg p-6 mb-6">
                    <h3 className="text-xl font-bold mb-3 text-yellow-300">
                      {getTemplatesByRelationship(gift.relationship)?.name}
                    </h3>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full text-sm font-bold">
                        {maxLanterns} رسالة حسب خطتك (
                        {gift.planType === "free"
                          ? "مجاني"
                          : gift.planType === "basic"
                            ? "أساسي"
                            : "مميز"}
                        )
                      </span>
                    </div>
                    <p className="text-purple-200 text-sm mb-4">
                      {getTemplatesByRelationship(
                        gift.relationship,
                      )?.getOpeningMessage(maxLanterns)}
                    </p>
                    <div className="bg-white/5 rounded-lg p-4 max-h-60 overflow-y-auto">
                      <p className="text-sm text-purple-300 mb-2">
                        عينة من الرسائل:
                      </p>
                      {getTemplatesByRelationship(gift.relationship)
                        ?.messages.slice(0, Math.min(5, maxLanterns))
                        .map((msg, idx) => (
                          <p key={idx} className="text-sm mb-2 text-white/80">
                            {idx + 1}. {msg}
                          </p>
                        ))}
                      {maxLanterns > 5 && (
                        <p className="text-xs text-purple-400 italic mt-2">
                          ... و {maxLanterns - 5} رسالة أخرى
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-4 mb-6">
                    <p className="text-yellow-200 text-sm">
                      ⚠️ <strong>تنبيه:</strong> سيتم ملء رسالة الافتتاح و
                      {maxLanterns} رسالة للأيام. يمكنك التعديل عليها لاحقاً.
                    </p>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={() => setShowTemplates(false)}
                      className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-lg"
                    >
                      إلغاء
                    </button>
                    <button
                      onClick={handleApplyTemplate}
                      className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-3 rounded-lg"
                    >
                      ✓ تطبيق القالب ({maxLanterns} رسالة)
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Live Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 overflow-y-auto">
          <div className="min-h-screen p-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex justify-between items-center mb-6 sticky top-4 bg-indigo-900/90 backdrop-blur-lg p-4 rounded-xl z-10">
                <h2 className="text-2xl font-bold">معاينة الهدية</h2>
                <button
                  onClick={() => setShowPreview(false)}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-bold transition-all"
                >
                  ✕ إغلاق
                </button>
              </div>

              {/* Preview Content */}
              <div
                className="bg-gradient-to-b from-indigo-950 via-purple-950 to-indigo-900 rounded-2xl p-8 text-right"
                dir="rtl"
              >
                <div className="text-center mb-12">
                  <Moon className="w-16 h-16 mx-auto mb-4 text-yellow-300" />
                  <h1 className="text-4xl font-bold mb-2">
                    إلى: {gift.recipientName || "اسم المستلم"}
                  </h1>
                  {gift.senderName && (
                    <h3 className="text-xl text-yellow-400/90 font-medium mb-4 flex items-center justify-center gap-2">
                      <span>من:</span>
                      <span>{gift.senderName}</span>
                      <Heart className="w-5 h-5 fill-current text-pink-500" />
                    </h3>
                  )}
                  <h2 className="text-2xl text-purple-200 mb-6">
                    Ramadan Kareem
                  </h2>
                  {gift.openingMessage && (
                    <div className="max-w-2xl mx-auto bg-white/10 backdrop-blur-lg rounded-lg p-6">
                      <p className="text-lg leading-relaxed">
                        {gift.openingMessage}
                      </p>
                    </div>
                  )}

                  {gift.hasPassword && gift.password && (
                    <div className="mt-6 bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-4 max-w-md mx-auto">
                      <p className="text-sm text-yellow-300">
                        🔒 هذه الهدية محمية بكلمة سر
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-3 md:grid-cols-5 gap-4 max-w-4xl mx-auto">
                  {gift.lanterns.slice(0, maxLanterns).map((lantern, idx) => (
                    <div
                      key={idx}
                      className={`aspect-square rounded-lg p-4 flex flex-col items-center justify-center gap-2 ${
                        lantern.message
                          ? "bg-gradient-to-br from-yellow-400/30 to-yellow-600/30"
                          : "bg-white/5 opacity-50"
                      }`}
                    >
                      <GiftIcon
                        className={`w-8 h-8 ${lantern.message ? "text-yellow-300" : "text-gray-500"}`}
                      />
                      <span className="text-2xl font-bold">{lantern.day}</span>
                      <span className="text-xs text-purple-200">
                        {lantern.message ? "✓" : "فارغ"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TikTok Follow Modal */}
      {showTikTokFollow && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <div
              className="bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 rounded-2xl p-8 text-center relative overflow-hidden"
              dir="rtl"
            >
              {/* خلفية متحركة */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-75"></div>
              </div>

              <div className="relative z-10">
                {/* أيقونة TikTok */}
                <div className="mb-6 flex justify-center">
                  <div className="bg-gradient-to-br from-pink-500 to-purple-600 p-4 rounded-full">
                    <svg className="w-16 h-16 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </div>
                </div>

                {/* العنوان */}
                <h2 className="text-3xl font-bold mb-4 text-white">
                  {hasClickedTikTok ? "شكراً لمتابعتك! 💝" : "خطوة أخيرة! 🎉"}
                </h2>

                {/* الوصف */}
                <p className="text-lg text-purple-200 mb-6 leading-relaxed">
                  {hasClickedTikTok 
                    ? "الآن يمكنك استلام هديتك المجانية"
                    : "لاستلام هديتك المجانية، يرجى متابعة صفحتنا على TikTok"
                  }
                </p>

                {/* زر المتابعة - يظهر فقط إذا لم يضغط بعد */}
                {!hasClickedTikTok && (
                  <a
                    href="https://www.tiktok.com/@two_2_souls"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setHasClickedTikTok(true)}
                    className="inline-block w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-bold py-4 px-6 rounded-xl mb-4 transition-all transform hover:scale-105 active:scale-95 shadow-lg"
                  >
                    <div className="flex items-center justify-center gap-3">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                      <span className="text-xl">متابعة @two_2_souls</span>
                    </div>
                  </a>
                )}

                {/* زر استلام الهدية - يظهر فقط بعد الضغط على TikTok */}
                {hasClickedTikTok && (
                  <button
                    onClick={async () => {
                      setShowTikTokFollow(false);
                      setHasClickedTikTok(false); // Reset للمرة القادمة
                      await proceedWithSave();
                    }}
                    disabled={isUploading}
                    className="w-full bg-yellow-400 hover:bg-yellow-500 text-indigo-950 font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105 active:scale-95 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-3 border-indigo-950 border-t-transparent rounded-full animate-spin"></div>
                        <span>جاري التجهيز...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-2xl">🎁</span>
                        <span className="text-xl">استلام الهدية</span>
                      </div>
                    )}
                  </button>
                )}

                {/* ملاحظة */}
                <p className="text-xs text-purple-300 mt-4">
                  {hasClickedTikTok 
                    ? "✨ شكراً لدعمك! يمكنك الآن الحصول على هديتك"
                    : "💡 سيتم فتح TikTok في صفحة جديدة، ثم ارجع هنا لاستلام هديتك"
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Developer Credit */}
      <div className="mt-8 group">
        <a
          href="https://www.tiktok.com/@two_2_souls"
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center gap-1 opacity-30 hover:opacity-100 transition-all"
        >
          <span className="text-[10px] tracking-[0.4em] text-gray-400 uppercase font-light">
            Developed by
          </span>
          <span className="text-white text-xs tracking-[0.3em] font-light group-hover:text-[#d4af37]">
            TWO <span className="font-bold">SOULS</span>
          </span>
        </a>
      </div>
    </div>
  );
};

export default GiftCreation;
